<script setup lang="ts">
import { ref, watch, defineProps, defineEmits } from 'vue';

const props = defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save', json: string): void;
}>();

const jsonText = ref('');
const error = ref<string | null>(null);

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    jsonText.value = '';
    error.value = null;
  }
});

const handleSave = () => {
  try {
    JSON.parse(jsonText.value);
    emit('save', jsonText.value);
    emit('close');
  } catch (e) {
    error.value = "Invalid JSON format.";
  }
};
</script>

<template>
  <transition name="fade">
    <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div class="bg-white w-full max-w-2xl rounded-lg shadow-xl border p-6 m-4 flex flex-col gap-4 transform transition-all">
        <div class="flex flex-col space-y-1.5 text-center sm:text-left">
          <h3 class="font-semibold leading-none tracking-tight text-lg">{{ $t('invoices.paste_json_title') }}</h3>
          <p class="text-sm text-slate-500">{{ $t('invoices.paste_json_desc') }}</p>
        </div>

        <div class="grid w-full gap-1.5">
          <textarea
            class="flex min-h-[300px] w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm ring-offset-white placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
            placeholder="{ ... }"
            v-model="jsonText"
          />
          <span v-if="error" class="text-sm text-red-500 font-medium">{{ error }}</span>
        </div>

        <div class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
          <button
            @click="emit('close')"
            class="inline-flex items-center justify-center rounded-md text-sm font-medium border border-slate-200 bg-white hover:bg-slate-100 h-10 px-4 py-2 mt-2 sm:mt-0"
          >
            {{ $t('invoices.cancel') }}
          </button>
          <button
            @click="handleSave"
            class="inline-flex items-center justify-center rounded-md text-sm font-medium bg-blue-600 text-slate-50 hover:bg-blue-600/90 h-10 px-4 py-2"
          >
            {{ $t('invoices.save') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>
